
import { Link } from 'react-router-dom';

interface CheckedBarBaseProps {
  count: number;
  onClear: () => void;
}

/* Mode when adding products – only the basic props are required */
interface AddProductsMode extends CheckedBarBaseProps {
  mode?: 'add-products';
  totalVisible?: never;
  onSelectAll?: never;
  onBulkDelete?: never;
  isBulkDeleting?: never;
  onExitBulkMode?: never;
}

/* Mode when bulk‑deleting – additional props become mandatory */
interface BulkDeleteMode extends CheckedBarBaseProps {
  mode: 'bulk-delete';
  totalVisible: number;
  onSelectAll: () => void;
  onBulkDelete: () => void;
  isBulkDeleting: boolean;
  onExitBulkMode: () => void;
}

/* Union type – TS will enforce that only the correct set of props can be passed */
type CheckedBarProps = AddProductsMode | BulkDeleteMode;

export function CheckedBar(props: CheckedBarProps) {
  const { count, onClear, mode = 'add-products' } = props;

  // ---------- Bulk‑delete mode ----------
  if (mode === 'bulk-delete') {
    // Safe cast because we know the shape when mode === 'bulk-delete'
    const {
      totalVisible,
      onSelectAll,
      onBulkDelete,
      isBulkDeleting,
      onExitBulkMode,
    } = props as BulkDeleteMode;

    return (
      <div className="fixed bottom-0 left-0 right-0 bg-[#0A0A0A] border-t border-white/10 shadow-2xl z-50 animate-slide-up">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 ${
                count > 0 ? 'bg-red-500' : 'bg-gray-600'
              } text-white font-bold text-sm flex items-center justify-center rounded`}
            >
              {count}
            </div>
            <div>
              <div className="text-white font-semibold text-sm">
                {count} {count === 1 ? 'product' : 'products'} selected for deletion
              </div>
              <div className="text-white/50 text-xs">
                {totalVisible} visible &middot; Click cards to select
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {count < totalVisible ? (
              <button
                onClick={onSelectAll}
                className="px-4 py-2 text-white/70 hover:text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap border border-white/10 rounded-lg hover:border-white/30"
              >
                Select All ({totalVisible})
              </button>
            ) : (
              <button
                onClick={onClear}
                className="px-4 py-2 text-white/70 hover:text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap border border-white/10 rounded-lg hover:border-white/30"
              >
                Deselect All
              </button>
            )}
            <button
              onClick={onBulkDelete}
              disabled={count === 0 || isBulkDeleting}
              className="px-6 py-2.5 bg-red-500 text-white font-semibold text-sm hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap rounded-lg disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isBulkDeleting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Deleting...
                </>
              ) : (
                <>
                  <i className="ri-delete-bin-line text-sm"></i>
                  Delete {count > 0 ? `(${count})` : ''}
                </>
              )}
            </button>
            <button
              onClick={onExitBulkMode}
              className="px-4 py-2.5 text-white/70 hover:text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap rounded-lg hover:bg-white/10"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Add‑products mode (default) ----------
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-[#0A0A0A] border-t border-white/10 shadow-2xl z-50">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#00FF88] text-black font-bold text-sm flex items-center justify-center">
            {count}
          </div>
          <div>
            <div className="text-white font-semibold text-sm">
              {count} {count === 1 ? 'product' : 'products'} selected
            </div>
            <div className="text-white/50 text-xs">Ready to add to your Products page</div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={onClear}
            className="px-4 py-2 text-white/70 hover:text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            Clear All
          </button>
          <Link
            to="/products"
            className="px-6 py-2.5 bg-[#00FF88] text-black font-semibold text-sm hover:bg-[#00FF88]/90 transition-colors cursor-pointer whitespace-nowrap"
          >
            View Products <i className="ri-arrow-right-line ml-1"></i>
          </Link>
        </div>
      </div>
    </div>
  );
}
